import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface AdTypeFrequency {
  enabled: boolean
  /** "bookend" is album-specific: instead of a repeating cadence, exactly one
   * ad goes before the first page, plus a second after the last page once the
   * album is at least `frequency_value` pages long. Reading an album is one
   * continuous work, not a feed being scanned, so a per-N rhythm through the
   * middle of it reads as interruption rather than inventory. */
  frequency_type: "per_items" | "per_page" | "bookend"
  /** For "bookend" this is the page-count threshold for the closing ad, not a
   * cadence — under it, the album gets the opening ad only. */
  frequency_value: number
  cooldown_minutes?: number // min minutes between ads of this type, 0/undefined = no cooldown
  /** Hard ceiling on how many slots of this type appear in a single grid
   * load, independent of `frequency_value`'s spacing. Without this, a long
   * infinite-scroll grid inserts a new slot every `frequency_value` posts
   * for the entire list — reported live as house ads feeling "crammed" /
   * "spammed" once a session scrolled past a couple hundred posts. Only
   * meaningful for inline_sponsored_ads today. */
  max_slots?: number
  /** When > 0, each slot rotates through the ad pool on this interval while
   * visible, instead of locking to one fixed creative for the whole grid
   * load. 0/undefined = static (default) — one ad per slot, unchanged until
   * the grid reloads. Only meaningful for inline_sponsored_ads today. */
  cycle_seconds?: number
  /** Whether this gutter ad dims (opacity-25, 700ms fade) while the video
   * beside it is actively playing, fading back to full opacity on pause —
   * so it doesn't compete for attention with content the user is actually
   * watching, but stays fully visible the rest of the time. Only meaningful
   * for letterbox_ads/pillarbox_ads. Previously hardcoded on in the booru
   * detail modal with no admin control, and entirely absent from the VR
   * page's gutter ads (they didn't dim at all, on any video, ever). */
  dim_while_playing?: boolean
}

export interface VideoPrerollFrequency extends AdTypeFrequency {
  skip_after_secs: number // 0 = non-skippable
  // Videos watched before the very first preroll ever shows in this browser
  // — a deliberately softer one-time grace period than the regular cadence
  // (frequency_value), so a new visitor doesn't feel ads immediately. Only
  // applies once per browser; every preroll after the first uses
  // frequency_value instead. See lib/ad-store.ts's shouldShowVideoAd().
  warmup_value: number
}

export interface AdFrequencyConfig {
  inline_sponsored_ads: AdTypeFrequency
  creator_script_promos: AdTypeFrequency
  upgrade_promos: AdTypeFrequency
  affiliate_ads: AdTypeFrequency
  // New placement-specific controls
  goonfolio_feed_ads: AdTypeFrequency      // community collections feed
  // NOT currently wired to anything — no component reads this key. Opening a
  // folder (My Folios detail view) has no ad slot today; this was a launch
  // decision (2026-09-13: Explore feed only, folder detail left ad-free for
  // now) rather than an oversight. Kept in the config/admin console so it's
  // ready to wire up later without a new migration if that decision changes.
  goonfolio_grid_ads: AdTypeFrequency      // inside open folder grid — UNUSED, see above
  bounty_list_ads: AdTypeFrequency         // bounty board list
  album_grid_ads: AdTypeFrequency          // inside an opened album/doujin page grid
  // Artists promoting their storefront (now creator_promotions) — used to
  // ride creator_script_promos' enable flag even though it's a different
  // product with its own Stripe checkout, so turning off script promos
  // silently killed paid commission promotions too. frequency_value here
  // isn't independently read anywhere — booru-grid.tsx still reuses
  // creator_script_promos' rotation slot/rhythm in the feed on purpose (same
  // placement) — only the on/off switch is separate now.
  commission_store_promos: AdTypeFrequency
  video_preroll: VideoPrerollFrequency     // pre-roll before videos play
  pause_overlay_ads: AdTypeFrequency       // corner card shown when a played video is paused
  // The two "slide-out" bars around letterboxed/pillarboxed media. Both had
  // NO frequency control of any kind: they rendered on every single media
  // open whose geometry happened to leave a bar, which is what makes them
  // read as spam. Like pause_overlay_ads these fire on a user action
  // (opening media) rather than a scroll cadence, so cooldown is the control
  // that matters, not frequency_value.
  letterbox_ads: AdTypeFrequency           // bar above/below landscape media
  pillarbox_ads: AdTypeFrequency           // gutters beside portrait media
  sticky_banner_ads: AdTypeFrequency       // fixed corner banner, sitewide
  video_slider_ads: AdTypeFrequency        // self-positioning video widget, sitewide
  in_page_push_ads: AdTypeFrequency        // simulated toast notification, sitewide
  instant_message_ads: AdTypeFrequency     // fake chat-bubble popup, sitewide
  outstream_video_ads: AdTypeFrequency     // in-feed autoplay-on-view video
  // Paid network display ads injected into the browse grid. Its OWN cadence: it used to be
  // derived as 3x the house-ad frequency (house ads are deliberately sparse), which put one real
  // ad every 90 posts and none at all before 90 posts were loaded.
  network_display_ads: AdTypeFrequency
  recommendation_widget_ads: AdTypeFrequency // "you may also like" native tiles
  // Fills an EMPTY Gooncave grid cell (before any content is added to it)
  // with a network ad, behind the "+ Add Content" prompt — never displaces
  // real content, since it only ever renders where a cell has none. Same
  // "always on, cooldown/frequency don't apply" shape as letterbox_ads —
  // this fires on cell state (empty vs. not), not a scroll cadence.
  gooncave_empty_cell_ads: AdTypeFrequency
}

interface AdFrequencyState {
  config: AdFrequencyConfig | null
  lastFetched: number
  fetchConfig: () => Promise<void>
  getFrequency: (adType: keyof AdFrequencyConfig) => number
  isEnabled: (adType: keyof AdFrequencyConfig) => boolean
  getCooldownMinutes: (adType: keyof AdFrequencyConfig) => number
  getMaxSlots: (adType: keyof AdFrequencyConfig) => number | undefined
  getCycleSeconds: (adType: keyof AdFrequencyConfig) => number
  getDimWhilePlaying: (adType: keyof AdFrequencyConfig) => boolean
}

const DEFAULT_CONFIG: AdFrequencyConfig = {
  inline_sponsored_ads: {
    enabled: true,
    frequency_type: "per_items",
    // This is the HOUSE-AD pool specifically (liveHouseAds in
    // booru-grid.tsx) — self-promotional cards for other parts of the app
    // (bounties/goonpals/commissions/discord/scripting), not paid network
    // inventory. A prior version of this comment called the whole pool
    // "upgrade to remove ads" cards; that was wrong — only one of the six
    // active rows (house_membership) is actually a membership upsell, now
    // flagged via house_ads.is_upgrade_promo and the only one suppressed
    // for an ad-free viewer (see app/api/ads/serve/route.ts). Kept
    // deliberately less frequent than a real ad slot would be: showing more
    // of these doesn't earn anything, it just repeats the same self-promo
    // more often.
    frequency_value: 30,
    // No lifetime cap on purpose — recurs every `frequency_value` posts for
    // the whole scroll session, not just near the top. `max_slots` left
    // unset here reads as "uncapped" at the call site in booru-grid.tsx
    // (`getMaxSlots(...) ?? Infinity`), matching injectAdsIntoPosts' own
    // default. A prior `max_slots: 2` meant exactly 2 ads total, always
    // near post #10/#20, then never again for the rest of the session —
    // a real behavioral gap from what "per 10 items" reads as, caught live
    // rather than assumed.
    cycle_seconds: 0,
  },
  creator_script_promos: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 40,
  },
  upgrade_promos: {
    enabled: true,
    frequency_type: "per_page",
    frequency_value: 2,
  },
  affiliate_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 75,
  },
  // This feed has no pagination (getPublicCollections() loads everything at
  // once), so "frequency" doesn't mean a repeating cadence here — it's a
  // single ad slot, shown once per page load, whenever an ad is available.
  // Previously frequency_value doubled as a minimum-collection-COUNT gate
  // (>= 15) that silently hid the ad entirely on any smaller Goonfolio —
  // launch decision was "1 ad, up 100% of the time," so that gate is gone;
  // only `enabled` does anything now. See components/collections-feed.tsx.
  goonfolio_feed_ads: {
    enabled: true,
    frequency_type: "per_page",
    frequency_value: 1,
  },
  goonfolio_grid_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 20,   // 1 ad per 20 images inside a folder
  },
  bounty_list_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 8,    // 1 ad per 8 bounty cards
  },
  // Bookended, not cadenced — see the frequency_type note above. One ad
  // before page 1 always; a closing one after the last page only for albums
  // of 10+ pages. A short album gets exactly one ad, total.
  album_grid_ads: {
    enabled: true,
    frequency_type: "bookend",
    frequency_value: 10,   // pages required before the closing ad is added
  },
  commission_store_promos: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 100,  // unused — see the interface comment above
  },
  video_preroll: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 6,    // every 6 videos after the warm-up — matches server default
    warmup_value: 4,       // no ad until the 4th video, ever, in this browser
    skip_after_secs: 5,
    cooldown_minutes: 0,
  },
  // Fires on a real user action (pause), not a cadence — cooldown is the
  // only control that matters. Defaults to a real cooldown (unlike the
  // 0/no-cooldown default above) since without one this would show on
  // every single pause.
  pause_overlay_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 1,
    cooldown_minutes: 20,
  },
  // Deliberately always-on, no cooldown — these are the "gutter" ads: they
  // only ever render alongside real media (booru-detail-modal.tsx,
  // app/vr/page.tsx) in the black bars a portrait/landscape mismatch
  // already leaves empty, never on top of content or a click target. That
  // non-intrusiveness is exactly why they're exempt from the throttling
  // every other placement gets — a deliberate product decision, not an
  // oversight.
  letterbox_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 1,
    cooldown_minutes: 0,
    dim_while_playing: true,
  },
  pillarbox_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 1,
    cooldown_minutes: 0,
    dim_while_playing: true,
  },
  // Sitewide fixed corner banner, not tied to any one post/video like the
  // others above — mounted once in app/layout.tsx (see StickyBannerAd) and
  // gated purely on this cooldown, same "own control layer on top of
  // whatever the network zone itself is configured to do" pattern as every
  // other placement here.
  sticky_banner_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 1,
    cooldown_minutes: 30,
  },
  // All new to this session (2026-09-02), all is_active:false in ad_zones
  // until wired and verified — see components/ads/VideoSliderAd.tsx,
  // InPagePushAd.tsx, InstantMessageAd.tsx. Same sitewide-cooldown pattern
  // as sticky_banner_ads above.
  video_slider_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 1,
    cooldown_minutes: 30,
  },
  in_page_push_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 1,
    cooldown_minutes: 30,
  },
  // Deliberately more conservative default cooldown — this format mimics a
  // real incoming chat message, a manipulative pattern by design. Flagged
  // for a real decision on whether to enable at all, not just how often.
  instant_message_ads: {
    enabled: false,
    frequency_type: "per_items",
    frequency_value: 1,
    cooldown_minutes: 60,
  },
  outstream_video_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 12,
    cooldown_minutes: 0,
  },
  network_display_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 12,
    cooldown_minutes: 0,
  },
  recommendation_widget_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 1,
    cooldown_minutes: 0,
  },
  gooncave_empty_cell_ads: {
    enabled: true,
    frequency_type: "per_items",
    frequency_value: 1,
    cooldown_minutes: 0,
  },
}

export const useAdFrequencyStore = create<AdFrequencyState>()(
  persist(
    (set, get) => ({
      config: null,
      lastFetched: 0,

      fetchConfig: async () => {
        const now = Date.now()
        const lastFetch = get().lastFetched

        // Cache for 5 minutes
        if (lastFetch && now - lastFetch < 5 * 60 * 1000) {
          return
        }

        try {
          const res = await fetch("/api/admin/ad-frequency")
          if (res.ok) {
            const data = await res.json()
            set({
              config: data.settings || DEFAULT_CONFIG,
              lastFetched: now,
            })
          } else {
            // Use default if fetch fails
            set({ config: DEFAULT_CONFIG, lastFetched: now })
          }
        } catch (error) {
          console.error("Failed to fetch ad frequency config:", error)
          set({ config: DEFAULT_CONFIG, lastFetched: now })
        }
      },

      getFrequency: (adType) => {
        const config = get().config || DEFAULT_CONFIG
        return (config[adType] ?? DEFAULT_CONFIG[adType]).frequency_value
      },

      isEnabled: (adType) => {
        const config = get().config || DEFAULT_CONFIG
        return (config[adType] ?? DEFAULT_CONFIG[adType]).enabled
      },

      getCooldownMinutes: (adType) => {
        const config = get().config || DEFAULT_CONFIG
        return (config[adType] ?? DEFAULT_CONFIG[adType]).cooldown_minutes ?? 0
      },

      getMaxSlots: (adType) => {
        const config = get().config || DEFAULT_CONFIG
        return (config[adType] ?? DEFAULT_CONFIG[adType]).max_slots
      },

      getCycleSeconds: (adType) => {
        const config = get().config || DEFAULT_CONFIG
        return (config[adType] ?? DEFAULT_CONFIG[adType]).cycle_seconds ?? 0
      },

      getDimWhilePlaying: (adType) => {
        const config = get().config || DEFAULT_CONFIG
        return (config[adType] ?? DEFAULT_CONFIG[adType]).dim_while_playing ?? true
      },
    }),
    {
      name: "goonru-ad-frequency",
      partialize: (state) => ({
        config: state.config,
        lastFetched: state.lastFetched,
      }),
    },
  ),
)
